import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Panel, SectionTitle, Text } from '../../../../components';
import { keywordReport, scoreTone, type KeywordCheck } from '../../keyword-checks';
import { resolveRouteKeywords, setRouteKeywords } from '../../seo-document';
import type { SiteSeoDocument, SiteSeoFooterRule, SiteSeoRoute } from '../../seo-types';
import type { LiveMetaEntry } from '../../site-seo-state';
import { FieldTag } from '../FieldTag';
import { KeywordsField } from '../KeywordsField';
import {
  Body,
  Checklist,
  Detail,
  FieldBlock,
  Head,
  Info,
  Mark,
  Prompt,
  Row,
  RowText,
  Score,
  TagRow,
} from './KeywordsPanel.styles';

export type KeywordsPanelProps = {
  draft: SiteSeoDocument;
  route: SiteSeoRoute;
  language: string;
  defaultLanguage: string;
  liveMeta: LiveMetaEntry | null;
  liveFooterRules: SiteSeoFooterRule[];
  footerOverridable: boolean;
  onChange: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
};

/** A check's sentence (each a literal, so the catalog extractor sees it). */
const Sentence = ({ check }: { check: KeywordCheck }): React.ReactElement => {
  switch (check.code) {
    case 'title-has-focus':
      return <Text text="Focus keyword in the title" tag="span" />;
    case 'title-starts-with-focus':
      return <Text text="Title starts with the focus keyword" tag="span" />;
    case 'description-has-focus':
      return <Text text="Focus keyword in the description" tag="span" />;
    case 'footer-first-paragraph':
      return <Text text="Focus keyword in the footer's first paragraph" tag="span" />;
    case 'footer-heading':
      return <Text text="Focus keyword in a footer heading" tag="span" />;
    case 'footer-density':
      return (
        <Text
          text="Keyword density {{density}}% (0.5–2.5%)"
          placeholders={{ density: check.detail?.density ?? 0 }}
          tag="span"
        />
      );
    case 'title-length':
      return <Text text="Title length 1–60" tag="span" />;
    case 'description-length':
      return <Text text="Description length 120–158" tag="span" />;
    case 'title-unique':
      return <Text text="Title not used by another page" tag="span" />;
    case 'description-unique':
      return <Text text="Description not used by another page" tag="span" />;
  }
};

/** What a check adds after its sentence: the length it measured, or the page that uses the same text. */
const CheckDetail = ({ check }: { check: KeywordCheck }): React.ReactElement | null => {
  const { length, duplicateOf } = check.detail ?? {};
  if (duplicateOf !== undefined)
    return <Detail text="Also on {{path}}" placeholders={{ path: duplicateOf }} tag="span" />;
  if (length !== undefined) return <Detail text="{{length}} characters" placeholders={{ length }} tag="span" />;
  return null;
};

/**
 * A route's keywords and how its texts use them. The field holds the route's own keywords in the selected language
 * (the default language's shown muted while it has none); the first is the focus keyword. The checklist scores the
 * texts the site shows against the focus: only a route's OWN focus scores it, never the site's keywords, so without
 * one the panel asks for it. Keywords are an authoring aid: never published, never an unpublished change.
 */
export const KeywordsPanel = ({
  draft,
  route,
  language,
  defaultLanguage,
  liveMeta,
  liveFooterRules,
  footerOverridable,
  onChange,
}: KeywordsPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const own = route.keywords?.[language] ?? [];
  const resolved = resolveRouteKeywords(draft, route.path, language, defaultLanguage);
  const inherited = resolved.source === 'default-language' ? resolved.keywords : undefined;
  const report = keywordReport(draft, {
    path: route.path,
    language,
    defaultLanguage,
    liveMeta: liveMeta?.status === 'ready' ? liveMeta.value : null,
    liveFooterRules,
    footerOverridable,
  });

  return (
    <Panel>
      <Head>
        <SectionTitle title="Focus keyword" />
        {report ? (
          <Score $tone={scoreTone(report.passed, report.total)} aria-label={t('Keyword score')}>
            {`${report.passed}/${report.total}`}
          </Score>
        ) : null}
      </Head>
      <Body>
        <FieldBlock data-field="keywords">
          <KeywordsField
            label="Keywords"
            placeholder="Add a keyword"
            value={own}
            inherited={inherited}
            onChange={(next) => onChange((d) => setRouteKeywords(d, route.path, language, next))}
          />
          {inherited ? (
            <TagRow>
              <FieldTag kind="using-default-language" language={defaultLanguage} />
            </TagRow>
          ) : null}
        </FieldBlock>
        {report ? (
          <>
            <Checklist aria-label={t('Keyword checklist')}>
              {report.checks.map((check) => (
                <Row key={check.code} data-check={check.code} data-ok={check.ok ? 'true' : 'false'}>
                  <Mark $ok={check.ok} role="img" aria-label={check.ok ? t('Passed') : t('Not yet')}>
                    <Icon name={check.ok ? 'check' : 'close'} size={14} />
                  </Mark>
                  <RowText>
                    <Sentence check={check} />
                    <CheckDetail check={check} />
                  </RowText>
                </Row>
              ))}
            </Checklist>
            <Info>
              <Row data-info="path-has-focus">
                <Mark $ok={report.pathHasFocus ? true : null}>
                  <Icon name={report.pathHasFocus ? 'check' : 'info'} size={14} />
                </Mark>
                <RowText>
                  <Text text="Address contains the focus keyword" tag="span" />
                  <Detail text="Addresses are fixed" tag="span" />
                </RowText>
              </Row>
              {report.secondariesUsed.total > 0 ? (
                <Row data-info="secondaries-used">
                  <Mark $ok={null}>
                    <Icon name="info" size={14} />
                  </Mark>
                  <RowText>
                    <Text
                      text="{{used}} of {{total}} secondary keywords appear in the texts"
                      placeholders={{ used: report.secondariesUsed.used, total: report.secondariesUsed.total }}
                      tag="span"
                    />
                  </RowText>
                </Row>
              ) : null}
            </Info>
          </>
        ) : (
          <Prompt text="Add a focus keyword to score this page" tag="paragraph" />
        )}
      </Body>
    </Panel>
  );
};

export default KeywordsPanel;
